<template>
    <tr :class="rowClasses" :style="rowStyle"
        @mouseover="$emit('mouseover', entry.property_id)"
        @mouseout="$emit('mouseout', entry.property_id)"
    >
        <td>
            <re-open-modal :re_key="entry.property_id" />
            <div class="pra-pills" :class="{ 'pra-pending': pending }">
                <div v-if="!state.hidePill"><span class="label label-rouded" :class="'label-' + state.theme">{{ $t(state.label) }}</span></div>
                <div v-if="entry.manual"><span class="label label-rouded pra-manual-badge">{{ $t("amas:analysis:pra:manual_added") }}</span></div>
                <div v-else-if="entry.overridden">
                    <span class="label label-rouded pra-manual-badge">
                        {{ $t("amas:analysis:pra:manual_edited") }}
                    </span>
                </div>
            </div>
        </td>
        <td class="text-center">
            <div class="pra-condition" v-tooltip="entry.depth === 0 ? null : $t('amas:analysis:pra:road_toggle_hint')">
                <true-false-selector
                    :value="!!entry.has_public_road"
                    :readonly="entry.depth === 0"
                    @select="$emit('override', entry.property_id, 'has_public_road', $event)"
                />
            </div>
        </td>
        <td class="text-center">
            <div v-if="entry.depth === 0" class="pra-condition-text text-muted" v-tooltip="ownersTooltip">{{ subjectText }}</div>
            <div v-else class="pra-condition">
                <true-false-selector
                    :value="entry.legal.ok"
                    @select="$emit('override', entry.property_id, 'has_legal_access', $event)"
                />
                <div class="pra-condition-text">
                    <span v-tooltip="legalTooltip">{{ legalText }}</span>
                    <span v-if="entry.same_owner && !entry.legal.ok" class="text-warning text-nowrap m-l-5" v-tooltip="$t('amas:analysis:pra:same_owner_hint')">
                        <i class="fa fa-exclamation-triangle"></i> {{ $t("amas:analysis:pra:same_owner") }}
                    </span>
                </div>
            </div>
        </td>
        <td class="text-center">
            <span v-if="entry.depth === 0" class="text-muted">-</span>
            <div v-else class="pra-condition">
                <true-false-selector
                    :value="entry.physical.ok"
                    @select="$emit('override', entry.property_id, 'has_physical_access', $event)"
                />
                <div class="pra-condition-text">{{ physicalText }}</div>
            </div>
        </td>
        <td>
            <span class="btn btn-xs btn-outline btn-rounded btn-info" :class="{ disabled: zkLoading }" @click.prevent="$emit('zk-extract', entry.property_id)">
                <i v-if="zkLoading" class="fa fa-spinner fa-spin"></i>
                {{ $t("amas:analysis:pra:zk_extract") }}
            </span>
        </td>
        <td>
            <a v-if="entry.manual" href="#" class="text-danger" v-tooltip="$t('amas:analysis:pra:remove_from_access')"
                @click.prevent="$emit('remove-step', entry.property_id)">
                {{ $t("general:remove") }}
            </a>
        </td>
    </tr>
</template>

<script>
import ReOpenModal from "@/components/widgets/ReOpenModal.vue";
import TrueFalseSelector from "./TrueFalseSelector.vue";
import { STATES } from "../states.js";

export default {
    components: { ReOpenModal, TrueFalseSelector },
    props: {
        entry: { type: Object, required: true },
        highlight: { type: Boolean, default: false },
        zkLoading: { type: Boolean, default: false },
        pending: { type: Boolean, default: false },
    },
    computed: {
        state() { return STATES[this.entry.state] || STATES.off_route; },
        ownerNames() {
            // ZK rows carry the address and a status ("- v stečaju") after the name and repeat holders per annotation.
            const names = (this.entry.owners || []).map(o => (o.name || "").split(", ")[0].split(" - ")[0].trim()).filter(Boolean);
            return names.filter((name, i) => names.indexOf(name) === i);
        },
        ownersTooltip() {
            if (this.ownerNames.length <= 1) { return null; }
            return this.ownerNames.join(", ");
        },
        legalTooltip() {
            const easement = (this.entry.legal || {}).easement;
            if (easement && easement.name) { return easement.name; }
            return this.ownersTooltip;
        },
        ownersSuffix() {
            const owners = this.ownerNames.length > 1 ? this.ownerNames[0] + " ..." : (this.ownerNames[0] || "");
            return owners ? " · " + this.$t("amas:analysis:pra:owner:{}").replace("{}", owners) : "";
        },
        subjectText() {
            return this.$t("amas:analysis:pra:reason_subject") + this.ownersSuffix;
        },
        legalText() {
            const legal = this.entry.legal || {};
            if (!legal.reason) { return "-"; }
            const easement = legal.easement || {};
            const owners = this.ownerNames.length > 1 ? this.ownerNames[0] + " ..." : (this.ownerNames[0] || "");
            const beneficiaries = easement.beneficiaries && easement.beneficiaries.length
                ? easement.beneficiaries
                : (easement.holder_re_key || "").split(";").map(k => k.trim()).filter(Boolean);
            let text = this.$t("amas:analysis:pra:reason_" + legal.reason)
                .replace("{owners}", owners)
                .replace("{code}", easement.code ? " (" + easement.code + ")" : "")
                .replace("{holder}", beneficiaries.join(", "))
                .replace(" ()", "");
            if (legal.reason === "manual") {
                text += this.ownersSuffix;
            }
            return text;
        },
        physicalText() {
            const physical = this.entry.physical || {};
            if (physical.border_length === null || physical.border_length === undefined) {
                return this.entry.manual ? "" : this.$t("amas:analysis:pra:no_geometry");
            }
            if (physical.border_length <= 1) { return this.$t("amas:analysis:pra:border_too_short"); }
            return this.$t("amas:analysis:pra:border_length:{}").replace("{}", physical.border_length.toFixed(1).replace(".", ","));
        },
        rowClasses() {
            return { "bg-light": this.highlight, "text-muted": this.entry.state === "off_route" };
        },
        rowStyle() { return { "border-left": "4px solid " + (this.state.rowColor || this.state.color) }; },
    },
}
</script>

<style>
.pra-condition { display: flex; flex-direction: column; align-items: center; gap: 3px; }
.pra-condition-text { font-size: 12px; line-height: 1.3; max-width: 340px; margin: 0 auto; }
.pra-condition .arvio-pra-true-false-selector .btn { padding: 0; }
.pra-pills > div { margin-top: 3px; }
.pra-pills.pra-pending { opacity: 0.4; transition: opacity 0.2s; }
.label.pra-manual-badge { background-color: #fff; color: #616b73; border: 1px solid #b8c0c7; }
</style>
